const CIVIL_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

const isLeapYear = (year) =>
  year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);

const daysInMonth = (year, month) => {
  if (month === 2) return isLeapYear(year) ? 29 : 28;
  return [4, 6, 9, 11].includes(month) ? 30 : 31;
};

export const parseCivilDate = (date) => {
  if (typeof date !== 'string') return null;
  const match = date.match(CIVIL_DATE_PATTERN);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);

  if (month < 1 || month > 12) return null;
  if (day < 1 || day > daysInMonth(year, month)) return null;

  return { year, month, day };
};

export const isValidCivilDate = (date) => parseCivilDate(date) !== null;

const pad = (value) => String(value).padStart(2, '0');

const formatUtcDate = (date) =>
  `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;

export const getFinancialDate = (record) => {
  if (!record || typeof record !== 'object') return null;
  if (isValidCivilDate(record.date)) return record.date;

  const legacyDate = record.data;
  if (isValidCivilDate(legacyDate)) return legacyDate;
  if (typeof legacyDate !== 'string' && typeof legacyDate !== 'number') {
    return null;
  }

  if (typeof legacyDate === 'string') {
    const leadingDate = legacyDate.slice(0, 10);
    if (!isValidCivilDate(leadingDate)) return null;
  }

  const parsed = new Date(legacyDate);
  if (Number.isNaN(parsed.getTime())) return null;
  return formatUtcDate(parsed);
};

export const getCivilYear = (date) => parseCivilDate(date)?.year ?? null;
export const getCivilMonth = (date) => parseCivilDate(date)?.month ?? null;

export const getYearMonthKey = (date) => {
  const parsed = parseCivilDate(date);
  return parsed ? `${parsed.month}/${parsed.year}` : null;
};

export const getCivilMonthRange = (yearMonthKey) => {
  const match = typeof yearMonthKey === 'string' && yearMonthKey.match(/^(\d{1,2})\/(\d{4})$/);
  if (!match) return null;
  const month = Number(match[1]);
  const year = Number(match[2]);
  if (month < 1 || month > 12) return null;
  return {
    startDate: `${year}-${pad(month)}-01`,
    endDate: `${year}-${pad(month)}-${pad(daysInMonth(year, month))}`,
  };
};
export const compareCivilDates = (left, right) => {
  if (!isValidCivilDate(left) || !isValidCivilDate(right)) return null;
  if (left === right) return 0;
  return left < right ? -1 : 1;
};

export const compareFinancialRecordsByDate = (left, right) => {
  const leftDate = getFinancialDate(left);
  const rightDate = getFinancialDate(right);
  if (!leftDate && !rightDate) return 0;
  if (!leftDate) return 1;
  if (!rightDate) return -1;
  return compareCivilDates(leftDate, rightDate);
};

export const isCivilDateInRange = (date, startDate = null, endDate = null) => {
  if (!isValidCivilDate(date)) return false;
  if (startDate && !isValidCivilDate(startDate)) return false;
  if (endDate && !isValidCivilDate(endDate)) return false;
  if (startDate && date < startDate) return false;
  if (endDate && date > endDate) return false;
  return true;
};

export const formatCivilDate = (date) => {
  const parsed = parseCivilDate(date);
  return parsed
    ? `${pad(parsed.day)}/${pad(parsed.month)}/${parsed.year}`
    : 'Data inválida';
};

export const compareYearMonthKeys = (left, right) => {
  const parseKey = (key) => {
    const match = typeof key === 'string' && key.match(/^(\d{1,2})\/(\d{4})$/);
    if (!match) return null;
    const month = Number(match[1]);
    const year = Number(match[2]);
    return month >= 1 && month <= 12 ? year * 12 + month : null;
  };
  const leftValue = parseKey(left);
  const rightValue = parseKey(right);
  if (leftValue === null && rightValue === null) return 0;
  if (leftValue === null) return 1;
  if (rightValue === null) return -1;
  return leftValue - rightValue;
};

export const getTodayCivilDate = (now = new Date()) =>
  `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
